import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { duration, ease } from '@/styles/motion';

export interface ContextMenuItem {
  label: string;
  onClick: () => void;
  icon?: string | React.ComponentType<any>;
  disabled?: boolean;
  danger?: boolean;
}

interface ContextMenuProps {
  isOpen: boolean;
  position: { x: number; y: number };
  items: ContextMenuItem[];
  onClose: () => void;
}

export const ContextMenu: React.FC<ContextMenuProps> = ({ isOpen, position, items, onClose }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjustedPosition, setAdjustedPosition] = useState(position);

  useEffect(() => {
    if (isOpen && menuRef.current) {
      const menuRect = menuRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - position.y;

      let newY = position.y;
      if (spaceBelow < menuRect.height && position.y > menuRect.height) {
        newY = position.y - menuRect.height;
      }

      setAdjustedPosition({ x: position.x, y: newY });
    }
  }, [isOpen, position]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          ref={menuRef}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.96 }}
          transition={{ duration: duration.instant, ease: ease.outExpo }}
          className="fixed z-50 w-auto bg-surface-containerHighest/50 backdrop-blur-md rounded-m3-medium shadow-elevation-2 py-2"
          style={{
            left: `${adjustedPosition.x}px`,
            top: `${adjustedPosition.y}px`,
          }}
        >
          {items.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                if (!item.disabled) {
                  item.onClick();
                  onClose();
                }
              }}
              disabled={item.disabled}
              className={`
                w-full px-3 py-1.5 text-left text-sm flex items-center gap-2 whitespace-nowrap text-surface-onVariant
                transition-colors duration-fast
                ${
                  item.disabled
                    ? 'opacity-50 cursor-not-allowed'
                    : item.danger
                      ? 'hover:bg-error-container [&]:text-error'
                      : 'hover:bg-primary-container text-primary'
                }
              `}
              aria-label={item.label}
            >
              {item.icon &&
                (typeof item.icon === 'string' ? (
                  <span className="text-lg">{item.icon}</span>
                ) : (
                  <span className="flex items-center justify-center w-5 h-5">
                    {React.createElement(item.icon, { size: 18 })}
                  </span>
                ))}
              <span>{item.label}</span>
            </button>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  );
};
